/**
 * SharedHeader - Top bar for the Kira App Shell.
 *
 * Displays the current module name (left), and indicators on the right:
 * sync status, Pomodoro mini-timer, and user avatar.
 *
 * The PomodoroIndicator is visible when a session is active (phase !== 'idle')
 * and shows remaining time with a glow animation. Clicking navigates to Focus module.
 *
 * Uses glassmorphism style to match the dark aesthetic.
 *
 * @see Requirements 1.5, 7.8
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useUIStore } from '@/stores/uiStore';
import { usePomodoroStore } from '@/stores/pomodoroStore';
import { useSyncStore } from '@/stores/syncStore';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/stores/toastStore';
import type { ModuleId } from '@/types/modules';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Map module IDs to human-readable display names. */
const MODULE_LABELS: Record<ModuleId, string> = {
  journal: 'Journal',
  tasks: 'Tasks',
  calendar: 'Calendar',
  habits: 'Habits',
  focus: 'Focus',
  countdown: 'Countdown',
  notes: 'Notes',
};

/** Format seconds into mm:ss display. */
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// ─── SyncIndicator ───────────────────────────────────────────────────────────

function SyncIndicator() {
  const { status, queueSize, flushNow } = useSyncStore();
  const [isFlushing, setIsFlushing] = useState<boolean>(false);

  const dotColor =
    status === 'synced'
      ? 'bg-green-400'
      : status === 'syncing' || status === 'offline'
        ? 'bg-yellow-400'
        : 'bg-red-400';

  const statusLabel =
    status === 'synced'
      ? 'Synced'
      : status === 'syncing'
        ? 'Syncing'
        : status === 'offline'
          ? 'Offline'
          : 'Sync error';

  const tooltipText =
    queueSize > 0 ? `${statusLabel} \u2014 ${queueSize} pending` : statusLabel;

  // Clickable whenever a flush fn is registered — even with an empty queue a
  // click should re-run the flush and refresh.
  const clickable = flushNow !== null;

  const handleSyncNow = useCallback(async () => {
    if (!flushNow || isFlushing) return;
    setIsFlushing(true);
    try {
      await flushNow();
      toast.success('Synced');
    } catch {
      toast.error('Sync failed \u2014 will retry');
    } finally {
      setIsFlushing(false);
    }
  }, [flushNow, isFlushing]);

  // Shared dot + tooltip markup so the clickable and non-clickable variants
  // stay visually identical.
  const dot = (
    <span
      className={`w-2.5 h-2.5 rounded-full ${dotColor} transition-colors duration-fast ${
        isFlushing ? 'animate-spin' : ''
      }`}
      aria-hidden="true"
    />
  );
  // When clickable with pending items, prompt the user to sync; with an empty
  // queue the action is simply "Sync now".
  const actionLabel =
    queueSize > 0 ? `Click to sync now \u2014 ${queueSize} pending` : 'Sync now';

  const tooltip = (
    <span
      className="absolute top-full mt-1 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-bg-secondary text-text-primary text-xs px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-fast pointer-events-none z-10 border border-white/10"
      role="tooltip"
    >
      {clickable ? actionLabel : tooltipText}
    </span>
  );

  if (clickable) {
    return (
      <button
        type="button"
        onClick={() => void handleSyncNow()}
        disabled={isFlushing}
        className="relative group flex items-center justify-center w-8 h-8 rounded-full hover:bg-white/5 transition-colors duration-fast cursor-pointer focus-visible:outline-2 focus-visible:outline-focus-ring focus-visible:outline-offset-2 disabled:cursor-default"
        aria-label={actionLabel}
      >
        {dot}
        {tooltip}
      </button>
    );
  }

  return (
    <div
      className="relative group flex items-center justify-center w-8 h-8"
      aria-label={tooltipText}
      role="status"
    >
      {dot}
      {tooltip}
    </div>
  );
}

// ─── PomodoroIndicator ───────────────────────────────────────────────────────

function PomodoroIndicator() {
  const { phase, remainingSeconds, isRunning } = usePomodoroStore();
  const setActiveModule = useUIStore((s) => s.setActiveModule);

  // Only show when a session is active (not idle)
  if (phase === 'idle') return null;

  const timeDisplay = formatTime(remainingSeconds);
  const ariaLabel = `Pomodoro timer: ${timeDisplay} remaining`;

  return (
    <button
      onClick={() => setActiveModule('focus')}
      className={`
        flex items-center gap-1.5 px-2.5 py-1 rounded-full
        text-sm font-medium text-purple-300
        bg-purple-500/20 border border-purple-400/30
        hover:bg-purple-500/30 hover:border-purple-400/50
        transition-all duration-300 cursor-pointer
        focus-visible:outline-2 focus-visible:outline-focus-ring focus-visible:outline-offset-2
        ${isRunning ? 'animate-pomodoro-glow' : ''}
      `}
      aria-label={ariaLabel}
    >
      {/* Small timer circle */}
      <span
        className={`
          w-2.5 h-2.5 rounded-full
          ${phase === 'work' ? 'bg-purple-400' : 'bg-cyan-400'}
          ${isRunning ? 'animate-pulse' : ''}
        `}
        aria-hidden="true"
      />
      <span className="tabular-nums">{timeDisplay}</span>
    </button>
  );
}

// ─── UserAvatar ──────────────────────────────────────────────────────────────

function UserAvatar() {
  const email = useAuthStore((s) => s.email);
  const logout = useAuthStore((s) => s.logout);

  const [open, setOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Derive initials from email or show default
  const initials = email ? email.charAt(0).toUpperCase() : '?';

  // Close on outside click and Escape key while the menu is open.
  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const handleSignOut = () => {
    setOpen(false);
    void logout();
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="
          w-8 h-8 rounded-full flex items-center justify-center
          bg-gradient-to-br from-purple-500/30 to-pink-500/30
          text-white text-sm font-semibold
          border border-white/10
          hover:border-purple-400/50 hover:shadow-[0_0_15px_rgba(192,132,252,0.2)]
          transition-all duration-300 cursor-pointer
          focus-visible:outline-2 focus-visible:outline-focus-ring focus-visible:outline-offset-2
        "
        aria-label="User settings"
        title="User settings"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {initials}
      </button>

      {open && (
        <div
          role="menu"
          aria-label="User menu"
          className="
            absolute right-0 top-full mt-2 w-56 z-20
            bg-bg-secondary border border-white/10 rounded-lg shadow-card
            overflow-hidden
          "
        >
          {/* User email (non-clickable) */}
          <div
            className="px-3 py-2 text-sm text-text-secondary truncate"
            title={email ?? undefined}
          >
            {email ?? 'Signed in'}
          </div>

          {/* Divider */}
          <div className="h-px bg-white/10" aria-hidden="true" />

          {/* Sign Out */}
          <button
            type="button"
            role="menuitem"
            onClick={handleSignOut}
            className="
              w-full text-left px-3 py-2 text-sm text-text-primary
              hover:bg-white/5 transition-colors duration-fast cursor-pointer
              focus-visible:outline-2 focus-visible:outline-focus-ring focus-visible:outline-offset-[-2px]
            "
          >
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
}

// ─── SharedHeader ────────────────────────────────────────────────────────────

export function SharedHeader() {
  const activeModule = useUIStore((s) => s.activeModule);
  const moduleLabel = MODULE_LABELS[activeModule] ?? 'Kira';

  return (
    <header
      className="h-14 border-b border-white/5 glass flex items-center justify-between px-4 tablet:px-6 shrink-0 relative z-30"
      role="banner"
    >
      {/* Left: Module name */}
      <div className="flex items-center gap-2">
        <h1 className="text-heading-3 text-white font-semibold leading-heading">
          {moduleLabel}
        </h1>
      </div>

      {/* Right: Indicators */}
      <div className="flex items-center gap-2">
        <SyncIndicator />
        <PomodoroIndicator />
        <UserAvatar />
      </div>
    </header>
  );
}
